import React, { useEffect, useRef, useState } from 'react';
import { IonGrid, IonRow, IonCol, IonList, IonItem, IonLabel, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonFab, IonFabButton, IonToast, IonLoading, IonBadge, IonImg } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import { search, arrowUpSharp } from 'ionicons/icons';
import { useHistory } from 'react-router';

// Hooks
import { useInfiniteScroll } from './../../hooks/useInfiniteScroll';

// Constants
import { APP_CONFIG } from '../../constants';

// Actions
import { startListTransactionsAction, startGetTransactionAction, setPageRefAction, resetErrorAction } from '../../redux';

// Helpers
import { statusTransactionHandler, convertToDateString, formatNumber, resolveColorMessageByStatusCode } from '../../helpers';

import './MobileHistoryComponent.scss';

export const MobileHistoryComponent = ({
    params,
    pagination,
    handlePageChange,
    showFabButton,
    contentRef,
    pageConfig,
    resetPagination
}) => {

    const dispatch = useDispatch();
    const { error } = useSelector(state => state.app);
    const { activated, list } = useSelector(state => state.transaction);
    const history = useHistory();
    const eventInfiniteScrollRef = useRef();
    const { disableInfiniteScroll, setDisableInfiniteScroll, showInfiniteScroll, setShowInfiniteScroll } = useInfiniteScroll();
    const [showToast, setShowToast] = useState({ show: false, error: null, status: null });
    const [loading, setLoading] = useState(false);

    useEffect(() => {

        setShowInfiniteScroll(true);

    }, []);

    /**
     * Cada vez que se inicializa list, debo reiniciar el paginado y el infiniteScroll
     */
    useEffect(() => {
        
        if ( list.items.length === 0 ) {

            resetPagination();
            setDisableInfiniteScroll(false);
            setShowInfiniteScroll(true);
        }
        
    }, [list]);

    /**
     * Cada vez que se habilita el infiniteScroll, oculto el scroll
     */
    useEffect(() => {

        if (disableInfiniteScroll) {

            setShowInfiniteScroll(false);
        }

    }, [disableInfiniteScroll]);

    /**
     * En caso de ocurrir cualquier error al llamar a la API; muestra el mensaje y deshabilita el infiniteScroll
     */
    useEffect(() => {

        if (error) {

            setShowToast({ show: true, error: error.message, status: error.status });
            dispatch(resetErrorAction());
        }

    }, [error]);

    /**
     * Cuando se termine el paginado, se debe deshabilitar el Infinite Scroll
     */
    useEffect(() => {

        if (list.metadata.nextKey === null) {
            setDisableInfiniteScroll(true);
        }

    }, [list]);

    /**
     * Maneja el cambio de página
     */
     useEffect(() => {

        if (pagination.page > 0 && activated.transactionId === '' && list.metadata.nextKey != null) {

            dispatch(startListTransactionsAction(params, pagination, setLoading));

            if (eventInfiniteScrollRef.current) {

                eventInfiniteScrollRef.current.target.complete();
            }
        }

    }, [pagination, activated.transactionId]);

    /**
     * Setea la referencia del infiniteScroll al solicitar una nueva pagina
     * @param {Event} e Evento del infinite scroll
     */
    const searchNext = async (e) => {

        handlePageChange();
        eventInfiniteScrollRef.current = e;
    }

    /**
     * Controla la referencia del scroll del container para la aparición del Fab
     */
    const handleClick = () => {

        contentRef.current.scrollToTop();
    };

    /**
     * Maneja la selección de una transacción
     * @param {Object} transaction Transacción de la que se mostrará el detalle
     */
    const handleSelectedTransaction = (transaction) => {

        dispatch(setPageRefAction(pageConfig.ref));
        // dispatch(resetTransactionAction());
        dispatch(startGetTransactionAction(transaction.transactionId, pageConfig, setLoading, history));
    };

    return (
        <>
            <IonLoading
                isOpen={loading}
                message={APP_CONFIG.CONTROLS.LOADING.MESSAGE}
            />
            <IonToast
                isOpen={showToast.show}
                onDidDismiss={() => setShowToast({ show: false, error: null })}
                message={showToast.error}
                duration={APP_CONFIG.CONTROLS.TOAST.DURATION}
                position={APP_CONFIG.CONTROLS.TOAST.POSITION}
                color={resolveColorMessageByStatusCode(showToast.status)}
            />
            <IonGrid className='ion-text-center'>
                <IonRow>
                    <IonCol>
                        {
                            !loading && list.items?.length === 0 &&

                            <IonGrid>
                                <IonRow className="grid-rows-3 state--msg">
                                    <IonCol>
                                        <p className='subtitle'>
                                            Aún no tenés transacciones para visualizar.
                                        </p>
                                    </IonCol>
                                    <IonCol className="text-align-center">
                                        <IonImg className="illustration" src="assets/images/no-history.png" />
                                    </IonCol>
                                </IonRow>
                            </IonGrid>

                        }

                        <IonList style={{ paddingBottom: "70px" }} >
                            {list.items?.map(transaction => {

                                const statusOptions = statusTransactionHandler(transaction.transactionStatus);
                                const updatedDate = convertToDateString(transaction.updatedDate);

                                return (
                                    <IonItem
                                        key={transaction.transactionId}
                                        onClick={() => handleSelectedTransaction(transaction)}
                                    >
                                        <IonLabel>
                                            <h1>{transaction.subscriber.name ? transaction.subscriber.name : `DNI: ${formatNumber(transaction.subscriber.dni)}`}</h1>
                                            <p>
                                                <IonBadge mode="ios" color={statusOptions.color}>
                                                    <IonIcon icon={statusOptions.icon}></IonIcon>
                                                </IonBadge>
                                                {statusOptions.description} {updatedDate && <> | {updatedDate}</>}
                                            </p>
                                        </IonLabel>
                                        <IonIcon icon={search} slot="end"></IonIcon>
                                    </IonItem>
                                );
                            })}
                        </IonList>
                    </IonCol>
                </IonRow>
            </IonGrid>

            {
                showFabButton &&
                <IonFab
                    vertical="bottom"
                    horizontal="center"
                    slot="fixed"
                >
                    <IonFabButton
                        onClick={handleClick}
                    >
                        <IonIcon icon={arrowUpSharp}></IonIcon>
                    </IonFabButton>
                </IonFab>
            }

            {
                showInfiniteScroll && list.items?.length > 0 &&
                <IonInfiniteScroll
                    threshold={APP_CONFIG.CONTROLS.INFINITE_SCROLL.SHOWIN}
                    disabled={disableInfiniteScroll}
                    onIonInfinite={searchNext}>
                    <IonInfiniteScrollContent
                        loading-spinner={APP_CONFIG.CONTROLS.INFINITE_SCROLL.LOADING_SPINNER}
                        loadingText={APP_CONFIG.CONTROLS.INFINITE_SCROLL.LOADING_TEXT}>
                    </IonInfiniteScrollContent>
                </IonInfiniteScroll>
            }

        </>
    )
}
